import { useState, useEffect, useCallback } from "react";
import { X, Users, Folder, Check, Loader2, AlertTriangle } from "lucide-react";
import type { User, Team, Project } from "@/types/admin";
import adminService from "@/services/admin-service";
import { Card, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";

interface MembershipState {
  isMember: boolean;
  role: string;
  originalRole: string | null; // null if not originally a member
}

interface UserMembershipsModalProps {
  user: User;
  teams: Team[];
  projects: Project[];
  onClose: () => void;
  onUpdate: () => void;
}

export default function UserMembershipsModal({
  user,
  teams,
  projects,
  onClose,
  onUpdate,
}: UserMembershipsModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current state of memberships (includes pending changes)
  const [teamStates, setTeamStates] = useState<Map<string, MembershipState>>(
    new Map(),
  );
  const [projectStates, setProjectStates] = useState<
    Map<string, MembershipState>
  >(new Map());

  const loadMemberships = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getUserMemberships(user.user_id);

      // Initialize team states
      const newTeamStates = new Map<string, MembershipState>();
      teams.forEach((team) => {
        const membership = data.teams.find((t) => t.team_id === team.team_id);
        newTeamStates.set(team.team_id, {
          isMember: !!membership,
          role: membership?.role || "member",
          originalRole: membership?.role || null,
        });
      });
      setTeamStates(newTeamStates);

      // Initialize project states
      const newProjectStates = new Map<string, MembershipState>();
      projects.forEach((project) => {
        const membership = data.projects.find(
          (p) => p.project_id === project.project_id,
        );
        newProjectStates.set(project.project_id, {
          isMember: !!membership,
          role: membership?.role || "viewer",
          originalRole: membership?.role || null,
        });
      });
      setProjectStates(newProjectStates);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load memberships");
      console.error("Error loading memberships:", err);
    } finally {
      setLoading(false);
    }
  }, [user.user_id, teams, projects]);

  useEffect(() => {
    loadMemberships();
  }, [loadMemberships]);

  // Toggle team membership
  const toggleTeam = (teamId: string) => {
    setTeamStates((prev) => {
      const newStates = new Map(prev);
      const current = newStates.get(teamId);
      if (current) {
        newStates.set(teamId, {
          ...current,
          isMember: !current.isMember,
          // Reset role to original or default when toggling back
          role: current.isMember
            ? current.originalRole || "member"
            : current.originalRole || "member",
        });
      }
      return newStates;
    });
  };

  // Update team role
  const updateTeamRole = (teamId: string, role: string) => {
    setTeamStates((prev) => {
      const newStates = new Map(prev);
      const current = newStates.get(teamId);
      if (current) {
        newStates.set(teamId, { ...current, role });
      }
      return newStates;
    });
  };

  // Toggle project membership
  const toggleProject = (projectId: string) => {
    setProjectStates((prev) => {
      const newStates = new Map(prev);
      const current = newStates.get(projectId);
      if (current) {
        newStates.set(projectId, {
          ...current,
          isMember: !current.isMember,
          role: current.isMember
            ? current.originalRole || "viewer"
            : current.originalRole || "viewer",
        });
      }
      return newStates;
    });
  };

  // Update project role
  const updateProjectRole = (projectId: string, role: string) => {
    setProjectStates((prev) => {
      const newStates = new Map(prev);
      const current = newStates.get(projectId);
      if (current) {
        newStates.set(projectId, { ...current, role });
      }
      return newStates;
    });
  };

  // Calculate pending changes
  const getTeamChanges = () => {
    const changes: Array<{
      teamId: string;
      action: "add" | "remove" | "update_role";
      role: string;
      teamName: string;
    }> = [];

    teamStates.forEach((state, teamId) => {
      const team = teams.find((t) => t.team_id === teamId);
      const teamName = team?.team_name || teamId;

      if (state.originalRole === null && state.isMember) {
        // Was not a member, now is -> add
        changes.push({ teamId, action: "add", role: state.role, teamName });
      } else if (state.originalRole !== null && !state.isMember) {
        // Was a member, now is not -> remove
        changes.push({ teamId, action: "remove", role: state.role, teamName });
      } else if (
        state.originalRole !== null &&
        state.isMember &&
        state.role !== state.originalRole
      ) {
        // Was a member, still is, but role changed -> update
        changes.push({
          teamId,
          action: "update_role",
          role: state.role,
          teamName,
        });
      }
    });

    return changes;
  };

  const getProjectChanges = () => {
    const changes: Array<{
      projectId: string;
      action: "add" | "remove" | "update_role";
      role: string;
      projectName: string;
    }> = [];

    projectStates.forEach((state, projectId) => {
      const project = projects.find((p) => p.project_id === projectId);
      const projectName = project?.project_name || projectId;

      if (state.originalRole === null && state.isMember) {
        // Was not a member, now is -> add
        changes.push({
          projectId,
          action: "add",
          role: state.role,
          projectName,
        });
      } else if (state.originalRole !== null && !state.isMember) {
        // Was a member, now is not -> remove
        changes.push({
          projectId,
          action: "remove",
          role: state.role,
          projectName,
        });
      } else if (
        state.originalRole !== null &&
        state.isMember &&
        state.role !== state.originalRole
      ) {
        // Was a member, still is, but role changed -> update
        changes.push({
          projectId,
          action: "update_role",
          role: state.role,
          projectName,
        });
      }
    });

    return changes;
  };

  const teamChanges = getTeamChanges();
  const projectChanges = getProjectChanges();
  const hasChanges = teamChanges.length > 0 || projectChanges.length > 0;
  const assignedTeamsCount = Array.from(teamStates.values()).filter(
    (s) => s.isMember,
  ).length;
  const assignedProjectsCount = Array.from(projectStates.values()).filter(
    (s) => s.isMember,
  ).length;

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);
      setError(null);

      // Process team changes
      for (const change of teamChanges) {
        if (change.action === "add") {
          await adminService.addUserToTeam(
            change.teamId,
            user.user_id,
            change.role,
          );
        } else if (change.action === "remove") {
          await adminService.removeUserFromTeam(change.teamId, user.user_id);
        } else if (change.action === "update_role") {
          // Remove and re-add with new role
          await adminService.removeUserFromTeam(change.teamId, user.user_id);
          await adminService.addUserToTeam(
            change.teamId,
            user.user_id,
            change.role,
          );
        }
      }

      // Process project changes
      for (const change of projectChanges) {
        if (change.action === "add") {
          await adminService.addUserToProject(
            change.projectId,
            user.user_id,
            change.role,
          );
        } else if (change.action === "remove") {
          await adminService.removeUserFromProject(
            change.projectId,
            user.user_id,
          );
        } else if (change.action === "update_role") {
          // Remove and re-add with new role
          await adminService.removeUserFromProject(
            change.projectId,
            user.user_id,
          );
          await adminService.addUserToProject(
            change.projectId,
            user.user_id,
            change.role,
          );
        }
      }

      // Reload memberships and notify parent
      await loadMemberships();
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save changes");
      console.error("Error saving memberships:", err);
    } finally {
      setSaving(false);
    }
  };

  const getChangeDescription = (
    action: "add" | "remove" | "update_role",
    role: string,
  ) => {
    switch (action) {
      case "add":
        return "Add to";
      case "remove":
        return "Remove from";
      case "update_role":
        return `Change role to "${role}" in`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="relative top-10 mx-auto max-w-5xl px-4 pb-10 sm:px-6">
        <Card shadow="xl" className="border-0 shadow-2xl">
          <div className="relative">
            {/* Gradient header */}
            <div className="h-20 rounded-t-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
              <div className="absolute -bottom-6 left-6 flex items-center gap-4">
                {user.user_picture ? (
                  <img
                    src={user.user_picture}
                    alt={user.user_name}
                    className="h-14 w-14 rounded-xl object-cover shadow-lg ring-4 ring-white dark:ring-gray-800"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-purple-700 text-lg font-bold text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                    {user.user_name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}
                <div className="pt-2">
                  <h3 className="text-lg font-bold text-white drop-shadow-lg">
                    {user.user_name}
                  </h3>
                  <p className="text-xs text-white/80">{user.user_email}</p>
                </div>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute right-3 top-3 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <CardBody className="px-6 pt-10">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Manage Memberships
              </h3>
              <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                Add or remove user from teams and projects
              </p>
            </div>

            {error && (
              <div className="mb-4 flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-800 dark:text-red-200">
                  {error}
                </p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Teams Section */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Teams
                    </h4>
                    <span className="text-xs text-gray-500">
                      ({assignedTeamsCount} assigned)
                    </span>
                  </div>
                  <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
                    {teams.map((team) => {
                      const state = teamStates.get(team.team_id);
                      if (!state) return null;

                      const isChanged =
                        (state.originalRole === null && state.isMember) ||
                        (state.originalRole !== null && !state.isMember) ||
                        (state.originalRole !== null &&
                          state.isMember &&
                          state.role !== state.originalRole);

                      return (
                        <div
                          key={team.team_id}
                          className={cn(
                            "flex items-center justify-between rounded-lg border px-3 py-2 transition-all",
                            state.isMember
                              ? "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20"
                              : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50",
                            isChanged && "ring-2 ring-yellow-400",
                          )}
                        >
                          <div className="flex items-center gap-2.5">
                            <button
                              onClick={() => toggleTeam(team.team_id)}
                              className={cn(
                                "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors",
                                state.isMember
                                  ? "border-blue-600 bg-blue-600 text-white"
                                  : "border-gray-300 hover:border-blue-400 dark:border-gray-600",
                              )}
                            >
                              {state.isMember && <Check className="h-3 w-3" />}
                            </button>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                {team.team_name}
                              </p>
                              <p className="truncate text-xs text-gray-500">
                                {team.description || "No description"}
                              </p>
                            </div>
                          </div>
                          {state.isMember && (
                            <select
                              value={state.role}
                              onChange={(e) =>
                                updateTeamRole(team.team_id, e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="ml-2 flex-shrink-0 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            >
                              <option value="member">Member</option>
                              <option value="team_admin">Admin</option>
                            </select>
                          )}
                        </div>
                      );
                    })}
                    {teams.length === 0 && (
                      <p className="py-4 text-center text-sm text-gray-500">
                        No teams available
                      </p>
                    )}
                  </div>
                </div>

                {/* Projects Section */}
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <Folder className="h-4 w-4 text-green-600" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Projects
                    </h4>
                    <span className="text-xs text-gray-500">
                      ({assignedProjectsCount} assigned)
                    </span>
                  </div>
                  <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
                    {projects.map((project) => {
                      const state = projectStates.get(project.project_id);
                      if (!state) return null;

                      const parentTeam = teams.find(
                        (t) => t.team_id === project.team_id,
                      );
                      const isChanged =
                        (state.originalRole === null && state.isMember) ||
                        (state.originalRole !== null && !state.isMember) ||
                        (state.originalRole !== null &&
                          state.isMember &&
                          state.role !== state.originalRole);

                      return (
                        <div
                          key={project.project_id}
                          className={cn(
                            "flex items-center justify-between rounded-lg border px-3 py-2 transition-all",
                            state.isMember
                              ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
                              : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50",
                            isChanged && "ring-2 ring-yellow-400",
                          )}
                        >
                          <div className="flex items-center gap-2.5">
                            <button
                              onClick={() => toggleProject(project.project_id)}
                              className={cn(
                                "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors",
                                state.isMember
                                  ? "border-green-600 bg-green-600 text-white"
                                  : "border-gray-300 hover:border-green-400 dark:border-gray-600",
                              )}
                            >
                              {state.isMember && <Check className="h-3 w-3" />}
                            </button>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                {project.project_name}
                              </p>
                              <p className="truncate text-xs text-gray-500">
                                {parentTeam?.team_name || "Unknown team"}
                              </p>
                            </div>
                          </div>
                          {state.isMember && (
                            <select
                              value={state.role}
                              onChange={(e) =>
                                updateProjectRole(
                                  project.project_id,
                                  e.target.value,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="ml-2 flex-shrink-0 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            >
                              <option value="viewer">Viewer</option>
                              <option value="editor">Editor</option>
                              <option value="project_admin">Admin</option>
                            </select>
                          )}
                        </div>
                      );
                    })}
                    {projects.length === 0 && (
                      <p className="py-3 text-center text-sm text-gray-500">
                        No projects available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Changes Summary */}
            {hasChanges && (
              <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Pending changes: {teamChanges.length + projectChanges.length}{" "}
                  modification(s)
                </p>
                <ul className="mt-1.5 space-y-0.5 text-xs text-yellow-700 dark:text-yellow-300">
                  {teamChanges.map((change) => (
                    <li key={change.teamId}>
                      • {getChangeDescription(change.action, change.role)} team:{" "}
                      {change.teamName}
                    </li>
                  ))}
                  {projectChanges.map((change) => (
                    <li key={change.projectId}>
                      • {getChangeDescription(change.action, change.role)}{" "}
                      project: {change.projectName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={cn(
                  "flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all",
                  hasChanges
                    ? "bg-gradient-to-r from-indigo-500 to-purple-600 shadow-md hover:from-indigo-600 hover:to-purple-700 hover:shadow-lg"
                    : "cursor-not-allowed bg-gray-400",
                )}
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </span>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
